import React from 'react';
import styled from 'styled-components';
 
import { useLanguage } from '../../i18n/LanguageContext';
import { Helmet } from 'react-helmet';
import Footer from '../../components/layout/Footer';
import { motion } from 'framer-motion';
import Button from '../../components/ui/Button';

// Background image for the page
const backgroundImage = 'https://images.unsplash.com/photo-1515263487990-61b07816b324?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1170&q=80';

const PageContainer = styled(motion.div)`
  background-color: #000;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const HeroSection = styled(motion.div)`
  background-color: #000;
  height: 20vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: white;
  position: relative;
  text-align: center;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.4);
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1200px;
  width: 100%;
  padding: 0 2rem;
`;

 

const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  font-style: italic;
  text-align: center;
  color: #0d9488;
`;

const ContentSection = styled.div`
  flex: 1;
  width: 100%;
  padding: 4rem 0;
  background-image: linear-gradient(rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.8)), url(${backgroundImage});
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  color: #fff;
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ServiceCard = styled.div`
  background-color: rgba(13, 148, 136, 0.1);
  border: 1px solid #0d9488;
  border-radius: 8px;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
  }
`;

const ServiceTitle = styled.h3`
  font-size: 1.8rem;
  color: #0d9488;
  margin-bottom: 1rem;
  font-style: italic;
`;

const ServiceDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  color: #e0e0e0;
  margin-bottom: 1.5rem;
  flex: 1;
`;

const CTASection = styled.div`
  margin-top: 6rem;
  text-align: center;
`;

const PopUp: React.FC = () => {
  const { language } = useLanguage();
  
  // Animation variants
  const pageVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 }
  };

  const heroVariants = {
    initial: { y: -50, opacity: 0 },
    animate: { y: 0, opacity: 1, transition: { duration: 0.6 } }
  };
  
  // Services data (only 3 cards)
  const services = [
    {
      title: language === 'it' ? 'POP-UP STORE' : 'POP-UP STORE',
      description: language === 'it' 
        ? 'Trasforma la tua proprietà in un negozio temporaneo: una soluzione agile per lanciare collezioni o testare nuovi mercati in location di prestigio.'
        : 'Turn your property into a temporary store: an agile solution to launch collections or test new markets in prestigious locations.',
      path: '/services/pop-up-store'
    },
    {
      title: language === 'it' ? 'SMART WORKING' : 'SMART WORKING',
      description: language === 'it' 
        ? 'Spazi pronti per il lavoro da remoto con Wi‑Fi veloce e postazioni ergonomiche, ideali per professionisti e team.'
        : 'Ready-to-work remote spaces with fast Wi‑Fi and ergonomic workstations, ideal for professionals and teams.',
      path: '/services/smart-working'
    },
    {
      title: language === 'it' ? 'SHOOTING' : 'SHOOTING',
      description: language === 'it' 
        ? 'Location versatili per servizi fotografici e video: valorizza gli ambienti unici dei tuoi spazi.'
        : 'Versatile locations for photo and video shoots: showcase the unique character of your spaces.',
      path: '/services/shooting'
    }
  ];
  
  return (
    <PageContainer
      initial="initial"
      animate="animate"
      exit="exit"
      variants={pageVariants}
    >
      <Helmet>
        <title>{language === 'it' ? 'Pop Up - Smart Working - Shooting | J-O-H' : 'Pop Up - Smart Working - Shooting | J-O-H'}</title>
        <meta 
          name="description" 
          content={language === 'it' 
            ? 'Soluzioni innovative per immobili in affitti brevi: pop‑up store, spazi per smart working e location per shooting foto/video.' 
            : 'Innovative solutions for short‑term rental properties: pop‑up stores, smart working spaces and photo/video shoot locations.'} 
        />
      </Helmet>
      
      <HeroSection variants={heroVariants}>
        <HeroContent>
          <PageTitle>POP UP - SMART WORKING - SHOOTING</PageTitle>
        </HeroContent>
      </HeroSection>
      
      <ContentSection>
        <ContentContainer>
          <ServicesGrid>
            {services.map((service, index) => (
              <ServiceCard key={index}>
                <ServiceTitle>{service.title}</ServiceTitle>
                <ServiceDescription>{service.description}</ServiceDescription>
                <Button variant="outline" to={service.path}>
                  {language === 'it' ? 'Scopri' : 'Discover'}
                </Button>
              </ServiceCard>
            ))}
          </ServicesGrid>
          
          <CTASection>
            <Button variant="primary" to="/contact">
              {language === 'it' ? 'Contattaci per maggiori informazioni' : 'Contact us for more information'}
            </Button>
          </CTASection>
        </ContentContainer>
      </ContentSection>
      
      <Footer />
    </PageContainer>
  );
};

export default PopUp; 